//@ts-nocheck
import React, { useEffect, useState } from "react";
import { Button, message, Tag, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  getPendingDealerResponses,
  approveDealerResponse,
  rejectDealerResponse,
  bulkApproveDealerResponses,
  bulkRejectDealerResponses,
  type DealerQuoteResponse,
} from "../../utils/API";
import NavigationDropdown from "../../Components/NavigationDropDown";

const PendingQuoteResponses: React.FC = () => {
  const [rows, setRows] = useState<DealerQuoteResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const data = await getPendingDealerResponses();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      message.error("Failed to load pending responses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleRow = (qid: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(qid) ? next.delete(qid) : next.add(qid);
      return next;
    });
  };

  const allChecked = rows.length > 0 && selected.size === rows.length;
  const someChecked = selected.size > 0 && selected.size < rows.length;

  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.qid)));
  };

  const handleApprove = async (qid: number) => {
    setApprovingId(qid);
    try {
      await approveDealerResponse(qid);
      message.success("Response approved");
      setRows((prev) => prev.filter((r) => r.qid !== qid));
      setSelected((prev) => { const n = new Set(prev); n.delete(qid); return n; });
    } catch {
      message.error("Failed to approve response");
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (qid: number) => {
    setRejectingId(qid);
    try {
      await rejectDealerResponse(qid);
      message.success("Response rejected");
      setRows((prev) => prev.filter((r) => r.qid !== qid));
      setSelected((prev) => { const n = new Set(prev); n.delete(qid); return n; });
    } catch {
      message.error("Failed to reject response");
    } finally {
      setRejectingId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (!selected.size) return;
    setBulkApproving(true);
    try {
      const qids = Array.from(selected) as number[];
      const res = await bulkApproveDealerResponses(qids);
      message.success(res.message || `${selected.size} response(s) approved`);
      setRows((prev) => prev.filter((r) => !selected.has(r.qid)));
      setSelected(new Set());
    } catch {
      message.error("Failed to bulk approve");
    } finally {
      setBulkApproving(false);
    }
  };

  const handleBulkReject = async () => {
    if (!selected.size) return;
    setBulkRejecting(true);
    try {
      const qids = Array.from(selected) as number[];
      const res = await bulkRejectDealerResponses(qids);
      message.success(res.message || `${selected.size} response(s) rejected`);
      setRows((prev) => prev.filter((r) => !selected.has(r.qid)));
      setSelected(new Set());
    } catch {
      message.error("Failed to bulk reject");
    } finally {
      setBulkRejecting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/", { replace: true });
  };

  const thStyle: React.CSSProperties = {
    background: "#ece8da",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 8px",
    borderBottom: "1px solid #6f6f6f",
    borderRight: "1px solid #6f6f6f",
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  const tdStyle: React.CSSProperties = {
    fontSize: 11,
    padding: "4px 8px",
    borderBottom: "1px solid #c0c0c0",
    borderRight: "1px solid #c0c0c0",
    verticalAlign: "top",
  };

  const hasSelection = selected.size > 0;

  return (
    <div style={{ background: "#f0f0f0", minHeight: "100vh" }}>

      {/* Header bar */}
      <div style={{ background: "#d4d0c8", borderBottom: "1px solid #a29f9f", padding: "4px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 36 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#000", whiteSpace: "nowrap" }}>
            Pending Quote Responses
          </span>
          <span style={{ color: "#999", fontSize: 12 }}>|</span>
          <NavigationDropdown className="!w-44" />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ height: 24, fontSize: 11, borderRadius: 0 }}>
            Refresh
          </Button>
          <Button size="small" danger onClick={handleLogout} style={{ height: 24, fontSize: 11, borderRadius: 0 }}>
            Logout
          </Button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "12px 16px" }}>

        {/* Info + bulk actions bar */}
        <div style={{ background: "#dcdcdc", border: "1px solid #a29f9f", padding: "6px 12px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 11, fontWeight: 600 }}>
            <span>
              Pending: <span style={{ color: "#8B0000", fontSize: 13 }}>{rows.length}</span>
            </span>
            {hasSelection && (
              <span style={{ color: "#555" }}>
                Selected: <strong>{selected.size}</strong>
              </span>
            )}
            {!hasSelection && (
              <span style={{ color: "#777", fontWeight: 400 }}>
                — Select rows to enable bulk actions
              </span>
            )}
          </div>

          {/* Bulk action buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              disabled={!hasSelection}
              loading={bulkApproving}
              onClick={handleBulkApprove}
              style={{
                height: 24,
                fontSize: 11,
                borderRadius: 0,
                background: hasSelection ? "#006400" : undefined,
                borderColor: hasSelection ? "#004d00" : undefined,
                padding: "0 10px",
              }}
            >
              Approve Selected ({selected.size})
            </Button>
            <Button
              size="small"
              danger
              icon={<CloseOutlined />}
              disabled={!hasSelection}
              loading={bulkRejecting}
              onClick={handleBulkReject}
              style={{ height: 24, fontSize: 11, borderRadius: 0, padding: "0 10px" }}
            >
              Reject Selected ({selected.size})
            </Button>
          </div>
        </div>

        {/* Table */}
        <div style={{ border: "1px solid #6f6f6f", background: "#fff", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 30, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                    onChange={toggleAll}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Company</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Brand</th>
                <th style={thStyle}>Model</th>
                <th style={{ ...thStyle, width: 90 }}>Options</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Qty</th>
                <th style={thStyle}>Price</th>
                <th style={thStyle}>Sales Comments</th>
                <th style={thStyle}>Date Quoted</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={12} style={{ ...tdStyle, textAlign: "center", padding: "20px", color: "#888" }}>
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ ...tdStyle, textAlign: "center", padding: "24px", color: "#888", fontStyle: "italic" }}>
                    No pending responses found
                  </td>
                </tr>
              )}
              {!loading && rows.map((row, idx) => {
                const isSelected = selected.has(row.qid);
                return (
                  <tr
                    key={row.qid}
                    style={{ background: isSelected ? "#e8f4e8" : idx % 2 === 0 ? "#ffffff" : "#f7f7f2", cursor: "pointer" }}
                    onClick={() => toggleRow(row.qid)}
                  >
                    <td style={{ ...tdStyle, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(row.qid)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>

                    <td style={{ ...tdStyle, color: "#555", width: 28 }}>{idx + 1}</td>

                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{row.companyName || "—"}</td>

                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {row.email ? (
                        <a href={`mailto:${row.email}`} style={{ color: "#004AAD", fontSize: 11 }} onClick={(e) => e.stopPropagation()}>
                          {row.email}
                        </a>
                      ) : "—"}
                    </td>

                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{row.eqBrand || "—"}</td>

                    <td style={{ ...tdStyle, fontWeight: 600, color: "#003bb3", whiteSpace: "nowrap" }}>{row.eqModel || "—"}</td>

                    <td style={{ ...tdStyle, width: 90, maxWidth: 90 }}>
                      <Tooltip title={row.options} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 90, cursor: row.options ? "help" : "default" }}>
                          {row.options || "—"}
                        </div>
                      </Tooltip>
                    </td>

                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.qty || "—"}</td>

                    <td style={{ ...tdStyle, fontWeight: 600, color: "#006400", whiteSpace: "nowrap" }}>
                      {row.priceString || (row.price != null ? String(row.price) : "—")}
                    </td>

                    <td style={{ ...tdStyle, maxWidth: 280 }}>
                      <Tooltip title={row.salesComments} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260, cursor: row.salesComments ? "help" : "default" }}>
                          {row.salesComments || "—"}
                        </div>
                      </Tooltip>
                    </td>

                    <td style={{ ...tdStyle, whiteSpace: "nowrap", color: "#555" }}>
                      {row.dateQuoted
                        ? dayjs(row.dateQuoted).isValid()
                          ? dayjs(row.dateQuoted).format("MM/DD/YYYY")
                          : row.dateQuoted
                        : "—"}
                    </td>

                    <td style={{ ...tdStyle, textAlign: "center", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          loading={approvingId === row.qid}
                          onClick={() => handleApprove(row.qid)}
                          style={{ height: 22, fontSize: 11, borderRadius: 0, background: "#006400", borderColor: "#004d00", padding: "0 8px" }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          danger
                          loading={rejectingId === row.qid}
                          onClick={() => handleReject(row.qid)}
                          style={{ height: 22, fontSize: 11, borderRadius: 0, padding: "0 8px" }}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PendingQuoteResponses;
